import React, { useState } from "react";
import { useIntl } from "react-intl";
import cn from "classnames";

// Mui
import {
    Box,
    Typography,
    TableRow,
    TableCell,
    IconButton,
    Tooltip,
    Collapse,
    Button,
    LinearProgress,
    ClickAwayListener,
    CircularProgress,
} from "@mui/material";

// Icons
import icons from "enums/icons";
import InfoIcon from "@mui/icons-material/Info";
import RestoreIcon from "@mui/icons-material/Restore";

// Utils
import { useAuth } from "utils/hooks";
import { getRetakeAssessmentDate } from "utils";
import { format, formatDistanceToNowStrict } from "date-fns";
import {
    AssessmentCentrePaceStatus,
    ConvertedAssessmentCentreUserData,
} from "pages/AssessmentCentre/adapters/converters";

// Styles
import UserTableRowStyles from "./UserTableRow.styles";

// Components
import { TeamChips } from "../TeamChips";
import CustomIcon from "components/CustomIcon";
import { InfoWithIcon } from "pages/ClientHub/components";
import NavButton from "components/HeaderNavigation/NavButton";
import PersonWorkHistory from "components/PersonWorkHistory";

// Types
import { paceStatusIcon } from "enums/paceStatus";

const UserTableRow = ({
    data,
    Checkbox,
    handleShowReport,
    handleDownloadReport,
    handleTransferProfile,
    handleGetInfo,
    handleSendReminder,
    requestProfileSharingId,
    handleRequestSharing,
}: {
    data: ConvertedAssessmentCentreUserData;
    Checkbox: React.ReactNode;
    handleShowReport: (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, userId: number) => void;
    handleDownloadReport: (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, userId: number) => void;
    handleTransferProfile: (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, userId: number) => void;
    handleGetInfo: (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, userId: number) => void;
    handleSendReminder: (userId: number) => void;
    requestProfileSharingId: number[];
    handleRequestSharing: (userId: number) => void;
}) => {
    const intl = useIntl();
    const classes = UserTableRowStyles();

    const { loggedInUserData } = useAuth();

    const [shouldExpand, setShouldExpand] = useState(false);
    const [openPaceInfo, setOpenPaceInfo] = useState(false);
    const [toggleShowWorkHistory, setToggleShowWorkHistory] = useState(false);
    const [WHAnchorEl, setWHAnchorEl] = useState<HTMLElement | null>(null);

    const hasLinkedin = Boolean(data.linkedinId);
    const isLoggedIdInUser = data.id === loggedInUserData.userId;
    const shouldShowReminderOption = data.paceStatus === "Incomplete" && !isLoggedIdInUser;

    const progress = data.assessments?.[0]?.progress?.meta?.meter?.percentage || 0;
    const lastCompletedAssessment = data.assessments.find((assessment) => assessment.meta.status === "Completed");
    const completedAssessmentCount = data.assessments.filter(
        (assessment) => assessment.meta.status === "Completed"
    ).length;

    const { isRetakeAvailable } = getRetakeAssessmentDate(lastCompletedAssessment?.meta.completed);
    const shouldShowRetakeOption = Boolean(completedAssessmentCount >= 1 && isRetakeAvailable && !isLoggedIdInUser);

    const handleToggleExpand = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        handleStopPropagation(event);
        setShouldExpand(!shouldExpand);
    };

    const handleTogglePaceInfo = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        handleStopPropagation(event);
        setOpenPaceInfo(!openPaceInfo);
    };

    const handleStopPropagation = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.stopPropagation();
    };

    const onClickRestoreIcon = (event: React.MouseEvent<HTMLElement>) => {
        if (!data?.personId) return;

        setToggleShowWorkHistory(true);
        setWHAnchorEl(event.currentTarget);
    };

    const onCloseWH = () => {
        setToggleShowWorkHistory(false);
        setWHAnchorEl(null);
    };

    const PaceInfo = ({ userId, status }: { userId: number; status: AssessmentCentrePaceStatus }) => {
        const isWaitingForRequest = requestProfileSharingId.includes(userId);

        return (
            <Box onClick={handleStopPropagation} className={classes.paceInfoWrapper}>
                <Box className={classes.paceInfoInner}>
                    {status === "Not Sharing" ? (
                        <>
                            <Box className={classes.paceInfoDescription}>
                                <Typography mb={2}>
                                    {intl.formatMessage({ id: "userManagement.sharePace.tooltip.content" })}
                                </Typography>
                                {data.lastContactedOn !== null && (
                                    <Typography mb={2}>
                                        Last Request Sent:{" "}
                                        {formatDistanceToNowStrict(new Date(data.lastContactedOn), { addSuffix: true })}
                                    </Typography>
                                )}
                            </Box>

                            <IconButton className={classes.paceInfoCloseButton} onClick={handleTogglePaceInfo}>
                                <CustomIcon icon={icons.close} />
                            </IconButton>
                        </>
                    ) : status === "Completed" ? (
                        <Typography>{intl.formatMessage({ id: "setup-company.team.takenPace" })}</Typography>
                    ) : (
                        status === "Incomplete" && (
                            <Typography>{intl.formatMessage({ id: "setup-company.team.notTakenPace" })}</Typography>
                        )
                    )}
                </Box>

                {status === "Not Sharing" && (
                    <Box display="flex" justifyContent="flex-end">
                        <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            onClick={() => handleRequestSharing(userId)}
                            sx={{ maxWidth: "auto", pointerEvents: isWaitingForRequest ? "none" : "auto" }}
                        >
                            {isWaitingForRequest ? (
                                <CircularProgress color="primary" size={16} />
                            ) : (
                                intl.formatMessage({ id: "userManagement.sharePace.tooltip.buttonLabel" })
                            )}
                        </Button>
                    </Box>
                )}
            </Box>
        );
    };

    const PaceStatus = ({ status, userId }: { status: AssessmentCentrePaceStatus; userId: number }) => {
        return (
            <InfoWithIcon
                entityName={data.userName}
                startSlot={
                    <Box display="flex" alignItems="center">
                        <Tooltip
                            arrow
                            enterDelay={500}
                            leaveDelay={200}
                            title={<PaceInfo userId={userId} status={status} />}
                            classes={{ popper: classes.paceInfoPopper }}
                        >
                            <IconButton sx={{ pl: 0 }}>
                                <CustomIcon icon={icons[paceStatusIcon[status]]} sx={{ fontSize: 20, mr: 1 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                }
            />
        );
    };

    return (
        <>
            <TableRow className={classes.row} onClick={(event) => handleToggleExpand(event)}>
                <TableCell>
                    <PaceStatus status={data.paceStatus} userId={data.id} />
                </TableCell>
                <TableCell>
                    <LinearProgress
                        value={progress}
                        variant="determinate"
                        className={cn([classes.progressBar], {
                            [classes.progressBarComplete]: progress > 99,
                        })}
                    />
                </TableCell>
                <TableCell>{Boolean(data.teams.length) && <TeamChips chips={data.teams} />}</TableCell>
                <TableCell>
                    {data.lastContactedOn !== null
                        ? formatDistanceToNowStrict(new Date(data.lastContactedOn), { addSuffix: true })
                        : ""}
                </TableCell>
                <TableCell>
                    {(shouldShowReminderOption || shouldShowRetakeOption) && (
                        <NavButton
                            className={classes.reminderButton}
                            icon={null}
                            size="small"
                            variant="outlined"
                            onClick={(ev) => {
                                ev.stopPropagation();
                                handleSendReminder(data.id);
                            }}
                        >
                            {shouldShowReminderOption ? "Send PACE Reminder" : "Send Retake Reminder"}
                        </NavButton>
                    )}
                </TableCell>
                <TableCell>{(shouldShowReminderOption || shouldShowRetakeOption) && Checkbox}</TableCell>
                <TableCell>
                    <Box display="flex" alignItems="center" justifyContent="flex-end">
                        {data.paceStatus === "Completed" && (
                            <>
                                <Tooltip
                                    arrow
                                    placement="bottom"
                                    title={intl.formatMessage({ id: "pace.admin.cardIcon.tooltip" })}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={(ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                                            handleShowReport(ev, data.id)
                                        }
                                    >
                                        <CustomIcon
                                            className={cn(classes.actionBtn, classes.actionBtn1)}
                                            icon={icons.chart}
                                        />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip arrow placement="bottom" title={intl.formatMessage({ id: "download.report" })}>
                                    <IconButton
                                        size="small"
                                        onClick={(ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                                            handleDownloadReport(ev, data.id)
                                        }
                                    >
                                        <CustomIcon
                                            className={cn(classes.actionBtn, classes.actionBtn1)}
                                            icon={icons.downloadFile}
                                        />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                        <IconButton
                            size="small"
                            onClick={(ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                                handleGetInfo(ev, data.id)
                            }
                        >
                            <InfoIcon className={cn(classes.actionBtn, classes.actionBtn2)} />
                        </IconButton>
                    </Box>
                </TableCell>
            </TableRow>
            <TableRow className={classes.expandableRow}>
                <TableCell colSpan={7}>
                    <Collapse in={shouldExpand} timeout="auto" unmountOnExit>
                        <Box display="flex" alignItems="center" justifyContent="space-between" py={1.2} px={0}>
                            <Box width="50%" display="flex" alignItems="center">
                                <IconButton
                                    size="small"
                                    sx={{ color: "primary.dark", p: 0, mr: 1 }}
                                    onClick={onClickRestoreIcon}
                                    disabled={!data.personId}
                                >
                                    <RestoreIcon sx={{ fontSize: 24 }} />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    sx={{ color: "primary.dark", p: 0, mr: 2 }}
                                    aria-label="linkedin button"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    disabled={!hasLinkedin}
                                    href={hasLinkedin ? `https://www.linkedin.com/in/${data.linkedinId}/` : "#"}
                                    onClick={handleStopPropagation}
                                >
                                    <CustomIcon icon={icons.linkedin} sx={{ fontSize: 20 }} />
                                </IconButton>
                                {data.email && (
                                    <Box display="flex" alignItems="center" mr={2} color="text.secondary">
                                        <Typography mr={1}>
                                            {intl.formatMessage({
                                                id: "hubManagement.client.userTable.email",
                                            })}
                                            :
                                        </Typography>
                                        <Typography>{data.email}</Typography>
                                    </Box>
                                )}
                                {hasLinkedin && (
                                    <Box display="flex" alignItems="center" color="text.secondary">
                                        <Typography mr={1}>
                                            {intl.formatMessage({
                                                id: "hubManagement.client.userTable.linkedInID",
                                            })}
                                            :
                                        </Typography>
                                        <Typography>{data.linkedinId}</Typography>
                                    </Box>
                                )}
                            </Box>
                            <Box
                                width="50%"
                                display="flex"
                                alignItems="center"
                                justifyContent="flex-end"
                                color="text.secondary"
                            >
                                <Typography mr={2}>{`No. of PACE Assessments: ${completedAssessmentCount}`}</Typography>
                                {lastCompletedAssessment?.meta.completed ? (
                                    <Typography mr={2}>{`PACE Last Taken: ${format(
                                        new Date(lastCompletedAssessment.meta.completed),
                                        "dd/MM/yyyy"
                                    )}`}</Typography>
                                ) : (
                                    <Typography mr={2}>Not Complete</Typography>
                                )}
                                <IconButton
                                    className={classes.toggleCloseBtn}
                                    onClick={(event) => handleToggleExpand(event)}
                                >
                                    <CustomIcon icon={icons.close} />
                                </IconButton>
                            </Box>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
            {toggleShowWorkHistory ? (
                <ClickAwayListener onClickAway={onCloseWH}>
                    <PersonWorkHistory
                        role={""}
                        personId={data.personId}
                        anchorEl={WHAnchorEl}
                        onClose={onCloseWH}
                        open={toggleShowWorkHistory}
                    />
                </ClickAwayListener>
            ) : null}
        </>
    );
};

export default UserTableRow;
